import { forwardRef, useEffect, useRef, useState } from "react";
import {
  AppWindow,
  Archive,
  BotMessageSquare,
  Columns2,
  EyeOff,
  Boxes,
  ChevronRight,
  Download,
  Ellipsis,
  FolderGit2,
  History,
  Gauge,
  LayoutTemplate,
  SquareKanban,
  LogOut,
  MessageCircleQuestion,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Radio,
  Rows2,
  ScrollText,
  WifiOff,
  Settings,
  User,
  Search,
  SquareTerminal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress, Separator } from "@/components/ui/misc";
import { Hint } from "@/components/ui/tooltip";
import { useAuth } from "@/lib/auth";
import { LIVE_OFF_LABEL, useLiveOff } from "@/lib/live";
import { useInstallOption } from "@/lib/pwa";
import { boardEndMs, byEndDescending, progressPercent, remainingLabel, urgencyColor } from "@/lib/format";
import { hasRole, ROLE_LABELS, type BoardDetail } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useLongPress } from "@/hooks/use-long-press";
import { setViewDrag } from "@/lib/window-intents";
import { myOpenTotal } from "@/lib/waiting";
import { isApplePlatform } from "@/lib/palette";

export type SidebarView =
  | { kind: "board"; boardId: string }
  /** "My tasks": the signed-in user's cards across boards. */
  | { kind: "queue" }
  | { kind: "activity" }
  /** Retired: the archive is a section of Settings. A stored one opens Settings. */
  | { kind: "archive" }
  /** Theme, dictation, the archive, members (admins) and the account. */
  | { kind: "settings" }
  /** Claude usage, plan limits, what is running, and the host. */
  | { kind: "status" }
  /** Every app on the host and the processes it runs, read-only. */
  | { kind: "apps" }
  /** The tab layout's board list screen; the sidebar is its own list. */
  | { kind: "boards" }
  /** Every card, across boards, waiting on the user's answer. Admin only. */
  | { kind: "waiting" }
  /** The registered directories work happens in. */
  | { kind: "projects" }
  /** What changed in each release (CHANGELOG.md, built into the bundle). */
  | { kind: "changelog" }
  /** Real-time shells on the host (xterm). Owner only. */
  | { kind: "terminal" }
  /** A desktop window not showing anything yet (Alt+N): a picker for what goes in it. */
  | { kind: "empty" }
  | { kind: "agents"; agentId?: string };

/** Folds the sidebar into the rail and back (or hides a strip), as `[` does — the palette's Toggle sidebar. */
export const TOGGLE_SIDEBAR_EVENT = "board:toggle-sidebar";

/** "⌘K" on an Apple device, "Ctrl K" elsewhere: the hint on the search buttons. */
const SEARCH_KEY = isApplePlatform(typeof navigator === "undefined" ? "" : navigator.platform || navigator.userAgent) ? "⌘K" : "Ctrl K";

/** The rail (collapsed) or the full sidebar (expanded), remembered across reloads. */
const COLLAPSED_STORAGE_KEY = "automation.sidebarCollapsed";
/** Whether the More group is open in the expanded sidebar. */
const MORE_STORAGE_KEY = "automation.sidebarMore";

function useStoredFlag(key: string): [boolean, (next: boolean | ((prev: boolean) => boolean)) => void] {
  const [value, setValue] = useState(() => {
    try {
      return localStorage.getItem(key) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, value ? "1" : "0");
    } catch {
      /* private mode: the choice lasts the session */
    }
  }, [key, value]);
  return [value, setValue];
}

const isEditable = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable || target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT");

/** One destination in the navigation, drawn as a row (expanded) or an icon (rail). */
type NavItem = {
  key: string;
  icon: React.ReactNode;
  label: string;
  count?: number;
  tint?: string;
  active: boolean;
  /**
   * A count that asks for something (cards assigned to you) rather than a tally
   * (how many projects exist). Only these are lifted onto a closed More group as
   * a pill; the tallies ride in its tooltip.
   */
  actionable?: boolean;
  /** Drawn in its tint, not muted: something is waiting on the user right now. */
  emphasis?: boolean;
  /** A second, separately labelled count: cards waiting on the user's review. */
  review?: number;
  view: SidebarView;
};

export function BoardSidebar({
  boards,
  view,
  now,
  live,
  onSelect,
  onCreateBoard,
  onArchiveBoard,
  projectCount,
  waitingAgents,
  awaitingAnswer,
  reviewCount,
  awaitingByBoard,
  dock = "left",
  windows,
  onSearch,
  className,
}: {
  /** Live boards only — archived ones live on their own page. */
  boards: BoardDetail[];
  view: SidebarView;
  now: number;
  /** True briefly after a change arrived from outside this tab (i.e. from Claude). */
  live: boolean;
  onSelect: (view: SidebarView) => void;
  onCreateBoard: () => void;
  /** Clears a board out of this list without deleting it; it lands in the archive. */
  onArchiveBoard: (detail: BoardDetail) => void;
  projectCount: number;
  /** Agents waiting on the user's answer. */
  waitingAgents: number;
  /** Cards waiting on the user's answer; undefined (below admin) hides the row. */
  awaitingAnswer?: number;
  /** Cards in a review column, waiting on the user to accept (the Awaiting page's "Ready for your review"). */
  reviewCount?: number;
  /** The same, per board, for a pill on each board row. */
  awaitingByBoard?: Map<string, number>;
  /** The screen edge it sits on: a column on the left or right, a strip along the top or bottom. */
  dock?: "left" | "right" | "top" | "bottom";
  /** The desktop workspace's hooks; absent in the tab layout, which has one view. */
  windows?: NavWindows;
  /** Opens the command palette. */
  onSearch?: () => void;
  className?: string;
}) {
  const { user, logout } = useAuth();
  const installOption = useInstallOption();
  const canEdit = hasRole(user, "member");
  const [collapsed, setCollapsed] = useStoredFlag(COLLAPSED_STORAGE_KEY);
  const [moreOpen, setMoreOpen] = useStoredFlag(MORE_STORAGE_KEY);
  // Board stats count cards assigned to the owner's row; anyone else's own queue
  // is still one click away, it just is not pre-counted here.
  const myTotal = myOpenTotal(user.id, boards);

  const horizontal = dock === "top" || dock === "bottom";
  const horizontalRef = useRef(horizontal);
  horizontalRef.current = horizontal;
  const windowsRef = useRef(windows);
  windowsRef.current = windows;

  // `[` (outside a text field) or Ctrl/Cmd+B folds the sidebar into the rail and back.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) return;
      const mod = event.metaKey || event.ctrlKey;
      const bracket = event.key === "[" && !mod && !event.altKey && !isEditable(event.target);
      const ctrlB = mod && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "b";
      if (!bracket && !ctrlB) return;
      event.preventDefault();
      // A strip has no rail to fold into, so the key puts it away (and brings it back).
      if (horizontalRef.current) windowsRef.current?.onHide();
      else setCollapsed((prev) => !prev);
    };
    const onToggle = () => {
      if (horizontalRef.current) windowsRef.current?.onHide();
      else setCollapsed((prev) => !prev);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener(TOGGLE_SIDEBAR_EVENT, onToggle);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(TOGGLE_SIDEBAR_EVENT, onToggle);
    };
  }, [setCollapsed]);

  /**
   * A board whose deadline has passed drops out of the running list into its own
   * group rather than disappearing: its cards are still the record of what did
   * not get finished. Nothing is written to say so — expiry is the board's own
   * `endsAt` against the clock, so a board that has its window extended comes
   * straight back up here.
   */
  const running: BoardDetail[] = [];
  const expired: BoardDetail[] = [];
  for (const detail of boards) {
    (boardEndMs(detail.board.endsAt) < now ? expired : running).push(detail);
  }
  expired.sort(byEndDescending);

  /**
   * The top group never moves: Agents, what they open all day, then what asks
   * for the user (Awaiting your answer, My tasks), then Status and Apps. Awaiting
   * is always there — muted at 0, amber while anything waits — because a row that
   * appears and disappears is one the eye has to hunt for.
   */
  const primary: NavItem[] = [
    {
      key: "agents",
      icon: <BotMessageSquare />,
      label: "Agents",
      count: waitingAgents,
      tint: waitingAgents > 0 ? "var(--waiting)" : "var(--kind-active)",
      active: view.kind === "agents",
      actionable: true,
      view: { kind: "agents" },
    },
    ...(awaitingAnswer !== undefined
      ? [
          {
            key: "waiting",
            icon: <MessageCircleQuestion />,
            label: "Awaiting your answer",
            count: awaitingAnswer,
            tint: "var(--waiting)",
            active: view.kind === "waiting",
            actionable: true,
            emphasis: awaitingAnswer > 0,
            view: { kind: "waiting" },
          } satisfies NavItem,
        ]
      : []),
    {
      key: "queue",
      icon: <User />,
      label: "My tasks",
      count: myTotal,
      review: reviewCount,
      tint: "var(--kind-active)",
      active: view.kind === "queue",
      actionable: true,
      view: { kind: "queue" },
    },
    { key: "status", icon: <Gauge />, label: "Status", active: view.kind === "status", view: { kind: "status" } },
    { key: "apps", icon: <Boxes />, label: "Apps", active: view.kind === "apps", view: { kind: "apps" } },
    // A shell on the host is the owner's alone; nobody else sees the row.
    ...(hasRole(user, "owner")
      ? [{ key: "terminal", icon: <SquareTerminal />, label: "Terminal", active: view.kind === "terminal", view: { kind: "terminal" } } satisfies NavItem]
      : []),
  ];
  /** The registers: looked at now and then, not all day. */
  const secondary: NavItem[] = [
    { key: "activity", icon: <ScrollText />, label: "Activity", active: view.kind === "activity", view: { kind: "activity" } },
    {
      // A project is the answer to "where does the work happen", which is board-wide.
      key: "projects",
      icon: <FolderGit2 />,
      label: "Projects",
      count: projectCount,
      tint: "var(--kind-review)",
      active: view.kind === "projects",
      view: { kind: "projects" },
    },
    { key: "changelog", icon: <History />, label: "Changelog", active: view.kind === "changelog", view: { kind: "changelog" } },
  ];
  const settingsActive = view.kind === "settings";
  const openSettings = () => onSelect({ kind: "settings" });
  const moreActive = secondary.find((item) => item.active);
  const moreAlerts = secondary.filter((item) => item.actionable && (item.count ?? 0) > 0);
  // Windows leads More on the desktop; its open count rides the tooltip so a closed More hides nothing.
  const moreSummary = [
    ...(windows ? [`Windows ${windows.count}`] : []),
    ...secondary.filter((item) => (item.count ?? 0) > 0).map((item) => `${item.label} ${item.count}`),
  ].join(" · ");

  const boardAwaiting = boards.reduce((sum, detail) => sum + (awaitingByBoard?.get(detail.board.id) ?? 0), 0);
  const boardOverdue = boards.reduce((sum, detail) => sum + detail.stats.overdue, 0);
  const activeBoardId = view.kind === "board" ? view.boardId : null;

  const liveOff = useLiveOff();
  const liveLabel = liveOff
    ? LIVE_OFF_LABEL
    : live
      ? "Just synced a change from Claude"
      : "Watching for changes from Claude";
  const collapseHint = collapsed ? "Expand sidebar ( [ )" : "Collapse sidebar ( [ )";

  const boardsMenu = {
    running,
    expired,
    total: boards.length,
    now,
    activeBoardId,
    awaitingByBoard,
    boardAwaiting,
    boardOverdue,
    canEdit,
    onSelect,
    onCreateBoard,
  };
  const moreMenu = { secondary, moreSummary, moreActive, moreAlerts, onSelect, windows };
  /** Menus and hints open away from the edge the navigation sits on. */
  const menuSide = dock === "right" ? "left" : dock === "top" ? "bottom" : dock === "bottom" ? "top" : "right";

  /**
   * With windows, a destination can be dragged onto the workspace to open it there,
   * and right-clicked (or held) for Open here / to the right / below. A plain click
   * opens it as a new tab of the focused window, beside the showing one (Open here
   * replaces; already open anywhere, it is shown where it is).
   */
  const viewDrag = (view: SidebarView): ViewDragProps =>
    windows
      ? {
          draggable: true,
          "data-nav-view": JSON.stringify(view),
          onDragStart: (event: React.DragEvent) => setViewDrag(event.dataTransfer, view),
        }
      : {};
  const [itemMenu, setItemMenu] = useState<{ view: SidebarView; x: number; y: number } | null>(null);
  const pressed = useRef<Element | null>(null);
  const hold = useLongPress({
    contextMenu: true,
    disabled: !windows,
    onLongPress: (point) => {
      const raw = pressed.current?.closest<HTMLElement>("[data-nav-view]")?.dataset.navView;
      if (!raw) return;
      setItemMenu({ view: JSON.parse(raw) as SidebarView, x: point.x, y: point.y });
    },
  });
  const holdHandlers = windows
    ? {
        ...hold,
        onPointerDownCapture: (event: React.PointerEvent) => {
          pressed.current = event.target as Element;
        },
        onContextMenuCapture: (event: React.MouseEvent) => {
          pressed.current = event.target as Element;
        },
      }
    : {};
  const pointMenu =
    windows && itemMenu ? (
      <DropdownMenu open onOpenChange={(open) => !open && setItemMenu(null)}>
        <DropdownMenuTrigger asChild>
          <span aria-hidden className="pointer-events-none fixed size-0" style={{ left: itemMenu.x, top: itemMenu.y }} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <DropdownMenuItem onSelect={() => windows.onOpen(itemMenu.view, "here")}>
            <AppWindow /> Open here
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => windows.onOpen(itemMenu.view, "right")}>
            <Columns2 /> Open to the right
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => windows.onOpen(itemMenu.view, "below")}>
            <Rows2 /> Open below
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;
  const windowsButton = (variant: "rail" | "row") =>
    windows ? windows.menu(<WindowsButton variant={variant} count={windows.count} />, menuSide) : null;
  /** Saved layouts one click away, after Apps: desktop only, since only the desktop has windows. */
  const layoutsButton = (variant: "rail" | "row") =>
    windows
      ? windows.layouts.menu(
          <LayoutsButton variant={variant} count={windows.layouts.count} activeName={windows.layouts.activeName} />,
          menuSide,
          variant === "rail" ? `Layouts${windows.layouts.activeName ? ` · ${windows.layouts.activeName}` : ""} (Alt+L next)` : undefined,
        )
      : null;
  const hideButton = windows ? (
    <Hint label="Hide navigation ( [ )" side={menuSide}>
      <Button variant="ghost" size="icon-sm" onClick={windows.onHide} aria-label="Hide navigation">
        <EyeOff />
      </Button>
    </Hint>
  ) : null;
  /** The logo is the handle: drag it to an edge of the screen to dock the navigation there. */
  const dockHandle = windows
    ? {
        onPointerDown: windows.onDockDragStart,
        className: "cursor-grab touch-none active:cursor-grabbing",
        title: "Drag to an edge of the screen to dock the navigation there",
      }
    : { className: "" };
  const searchButton = onSearch ? (
    <Hint label={`Search (${SEARCH_KEY})`} side={menuSide}>
      <Button variant="ghost" size="icon-sm" onClick={onSearch} aria-label="Search" aria-keyshortcuts="Meta+K Control+K">
        <Search />
      </Button>
    </Hint>
  ) : null;
  const liveMark = (
    <span className={cn("relative grid size-7 shrink-0 place-items-center", dockHandle.className)} onPointerDown={dockHandle.onPointerDown}>
      <img src="/favicon.svg" alt="" draggable={false} className="size-7 rounded-md" />
      {liveOff ? (
        <WifiOff className="absolute -bottom-1 -right-1 size-3 rounded-full bg-surface text-muted-foreground" />
      ) : live ? (
        <span className="absolute -right-0.5 -top-0.5 size-2 animate-pulse rounded-full bg-primary" />
      ) : null}
    </span>
  );

  if (horizontal) {
    return (
      <>
        <aside
          className={cn(
            "flex h-12 w-full shrink-0 items-center gap-1 overflow-x-auto border-border/70 bg-surface/50 px-2 scrollbar-none",
            dock === "top" ? "border-b" : "border-t",
            className,
          )}
          aria-label="Navigation"
          {...holdHandlers}
        >
          <Hint label={`Board · ${liveLabel}`} side={menuSide}>
            {liveMark}
          </Hint>
          <span className="mx-1 h-6 w-px shrink-0 bg-border/70" aria-hidden />
          {searchButton}
          <nav className="flex items-center gap-1">
            {primary.map((item) => (
              <Hint key={item.key} label={railLabel(item)} side={menuSide}>
                <RailButton item={item} onClick={() => onSelect(item.view)} {...viewDrag(item.view)} />
              </Hint>
            ))}
            {layoutsButton("rail")}
            <BoardsDropdown {...boardsMenu} side={menuSide} align="start" />
          </nav>
          <div className="flex-1" />
          <MoreDropdown {...moreMenu} side={menuSide} align="end" />
          <span className="mx-1 h-6 w-px shrink-0 bg-border/70" aria-hidden />
          {installOption ? (
            <Hint label={installOption.label} side={menuSide}>
              <Button variant="ghost" size="icon-sm" onClick={installOption.install} aria-label={installOption.label}>
                <Download />
              </Button>
            </Hint>
          ) : null}
          <Hint label="Settings" side={menuSide}>
            <RailButton
              item={{ key: "settings", icon: <Settings />, label: "Settings", active: settingsActive, view: { kind: "settings" } }}
              onClick={openSettings}
            />
          </Hint>
          <Hint label="Sign out" side={menuSide}>
            <Button variant="ghost" size="icon-sm" onClick={() => void logout()} aria-label="Sign out">
              <LogOut />
            </Button>
          </Hint>
          <Hint label={[user.displayName, user.role ? ROLE_LABELS[user.role] : "", user.email, "Settings"].filter(Boolean).join(" · ")} side={menuSide}>
            <button
              type="button"
              onClick={openSettings}
              aria-label={`${user.displayName} — settings`}
              className="grid size-7 shrink-0 place-items-center rounded-full bg-muted text-[10.5px] font-semibold text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              {initials(user.displayName)}
            </button>
          </Hint>
          {hideButton}
        </aside>
        {pointMenu}
      </>
    );
  }

  return (
    <>
    <aside
      className={cn(
        "motion-rail flex shrink-0 flex-col overflow-hidden border-border/70 bg-surface/50",
        dock === "right" ? "order-last border-l" : "border-r",
        collapsed ? "w-14" : "w-64",
        className,
      )}
      aria-label="Navigation"
      {...holdHandlers}
    >
      {collapsed ? (
        <div className="flex h-full w-14 flex-col items-center">
          <div className="flex flex-col items-center gap-1.5 py-3">
            <Hint label={`Board · ${liveLabel}`} side={menuSide}>
              {liveMark}
            </Hint>
            <Hint label={collapseHint} side={menuSide}>
              <Button variant="ghost" size="icon-sm" onClick={() => setCollapsed(false)} aria-label="Expand sidebar">
                <PanelLeftOpen className={cn(dock === "right" && "-scale-x-100")} />
              </Button>
            </Hint>
            {hideButton}
            {searchButton}
          </div>

          <Separator />

          <nav className="flex flex-col items-center gap-1 py-2">
            {primary.map((item) => (
              <Hint key={item.key} label={railLabel(item)} side={menuSide}>
                <RailButton item={item} onClick={() => onSelect(item.view)} {...viewDrag(item.view)} />
              </Hint>
            ))}
            {layoutsButton("rail")}

            <BoardsDropdown {...boardsMenu} side={menuSide} align="start" />
          </nav>

          <div className="flex-1" />

          <div className="flex flex-col items-center py-2">
            <MoreDropdown {...moreMenu} side={menuSide} align="end" />
          </div>

          <Separator />

          <div className="flex flex-col items-center gap-1 py-2.5">
            {installOption ? (
              <Hint label={installOption.label} side={menuSide}>
                <Button variant="ghost" size="icon-sm" onClick={installOption.install} aria-label={installOption.label}>
                  <Download />
                </Button>
              </Hint>
            ) : null}
            <Hint label="Settings" side={menuSide}>
              <RailButton
                item={{ key: "settings", icon: <Settings />, label: "Settings", active: settingsActive, view: { kind: "settings" } }}
                onClick={openSettings}
              />
            </Hint>
            <Hint label="Sign out" side={menuSide}>
              <Button variant="ghost" size="icon-sm" onClick={() => void logout()} aria-label="Sign out">
                <LogOut />
              </Button>
            </Hint>
            <Hint
              label={[user.displayName, user.role ? ROLE_LABELS[user.role] : "", user.email, "Settings"].filter(Boolean).join(" · ")}
              side={menuSide}
            >
              <button
                type="button"
                onClick={openSettings}
                aria-label={`${user.displayName} — settings`}
                className="mt-1 grid size-7 place-items-center rounded-full bg-muted text-[10.5px] font-semibold text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                {initials(user.displayName)}
              </button>
            </Hint>
          </div>
        </div>
      ) : (
        // A fixed-width inner column, so the rows are clipped rather than rewrapped
        // while the width animates.
        <div className="flex h-full w-64 flex-col">
          <div className="flex items-center gap-2 py-3.5 pl-4 pr-2.5">
            <img
              src="/favicon.svg"
              alt=""
              draggable={false}
              className={cn("size-7 rounded-md", dockHandle.className)}
              onPointerDown={dockHandle.onPointerDown}
              title={windows ? dockHandle.title : undefined}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold tracking-tight">Board</p>
              <p className="truncate text-[10.5px] text-muted-foreground">time-boxed kanban</p>
            </div>
            <Hint label={liveLabel}>
              <span
                className={cn(
                  "inline-flex size-6 items-center justify-center rounded-full transition-colors",
                  liveOff ? "text-muted-foreground" : live ? "text-primary" : "text-muted-foreground/50",
                )}
              >
                {liveOff ? <WifiOff className="size-3.5" /> : <Radio className={cn("size-3.5", live && "animate-pulse")} />}
              </span>
            </Hint>
            {searchButton}
            <Hint label={collapseHint}>
              <Button variant="ghost" size="icon-sm" onClick={() => setCollapsed(true)} aria-label="Collapse sidebar">
                <PanelLeftClose className={cn(dock === "right" && "-scale-x-100")} />
              </Button>
            </Hint>
            {hideButton}
          </div>

          <Separator />

          <nav className="space-y-0.5 p-2">
            {primary.map((item) => (
              <NavRow key={item.key} item={item} onClick={() => onSelect(item.view)} drag={viewDrag(item.view)} />
            ))}
            {layoutsButton("row")}
          </nav>

          <Separator />

          <div className="flex items-center justify-between px-3 pb-1 pt-2.5">
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
              Boards
              <span className="ml-1.5 font-normal opacity-70">{running.length}</span>
            </p>
            <Hint label={canEdit ? "New board" : "Viewers cannot create boards"}>
              <Button variant="ghost" size="icon-sm" onClick={onCreateBoard} disabled={!canEdit} aria-label="New board">
                <Plus />
              </Button>
            </Hint>
          </div>

          <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 pb-3 scrollbar-slim">
            {boards.length === 0 ? (
              <p className="px-2 py-4 text-xs text-muted-foreground">
                No boards yet. Create one, or ask Claude to make it for you.
              </p>
            ) : null}

            {running.length === 0 && expired.length > 0 ? (
              <p className="px-2 py-3 text-xs text-muted-foreground">
                Every board's window has closed. Start a new one, or reopen one below.
              </p>
            ) : null}

            {running.map((detail) => (
              <BoardRow
                key={detail.board.id}
                detail={detail}
                now={now}
                active={activeBoardId === detail.board.id}
                awaiting={awaitingByBoard?.get(detail.board.id) ?? 0}
                onSelect={() => onSelect({ kind: "board", boardId: detail.board.id })}
                drag={viewDrag({ kind: "board", boardId: detail.board.id })}
              />
            ))}

            {expired.length > 0 ? (
              <>
                <div className="flex items-center gap-2 px-2.5 pb-1 pt-3">
                  <p className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Expired
                    <span className="ml-1.5 font-normal opacity-70">{expired.length}</span>
                  </p>
                  <span className="h-px flex-1 bg-border/70" />
                </div>
                {expired.map((detail) => (
                  <BoardRow
                    key={detail.board.id}
                    detail={detail}
                    now={now}
                    active={activeBoardId === detail.board.id}
                    awaiting={awaitingByBoard?.get(detail.board.id) ?? 0}
                    onSelect={() => onSelect({ kind: "board", boardId: detail.board.id })}
                    onArchive={() => onArchiveBoard(detail)}
                    drag={viewDrag({ kind: "board", boardId: detail.board.id })}
                  />
                ))}
              </>
            ) : null}
          </div>

          <Separator />

          {/* The registers, folded away at the bottom. Closed, it still says which
              one is open and lifts any count that asks for something. */}
          <div className="p-2">
            <button
              type="button"
              onClick={() => setMoreOpen((prev) => !prev)}
              aria-expanded={moreOpen}
              title={!moreOpen && moreSummary ? moreSummary : undefined}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors",
                "[&_svg]:size-4 [&_svg]:shrink-0",
                !moreOpen && moreActive
                  ? "bg-accent/50 font-medium text-accent-foreground"
                  : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
              )}
            >
              <Ellipsis />
              <span className="min-w-0 flex-1 truncate text-left">
                More
                {!moreOpen && moreActive ? (
                  <span className="font-normal text-muted-foreground"> · {moreActive.label}</span>
                ) : null}
              </span>
              {!moreOpen ? moreAlerts.map((item) => <CountPill key={item.key} count={item.count} tint={item.tint} />) : null}
              <ChevronRight className={cn("motion-chevron text-muted-foreground/70", moreOpen && "rotate-90")} />
            </button>
            {moreOpen ? (
              <div className="mt-0.5 space-y-0.5">
                {windowsButton("row")}
                {secondary.map((item) => (
                  <NavRow key={item.key} item={item} onClick={() => onSelect(item.view)} drag={viewDrag(item.view)} />
                ))}
              </div>
            ) : null}
          </div>

          <Separator />
          <div className="flex items-center gap-1 py-2 pl-1.5 pr-3">
            {/* The user chip is the way into Settings too, as in most apps. */}
            <button
              type="button"
              onClick={openSettings}
              title="Settings"
              className={cn(
                "min-w-0 flex-1 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-muted/50",
                settingsActive && "bg-accent/50",
              )}
            >
              <p className="truncate text-[12.5px] font-medium">{user.displayName}</p>
              <p className="truncate text-[10.5px] text-muted-foreground">
                {user.role ? ROLE_LABELS[user.role] : ""} · {user.email}
              </p>
            </button>
            {installOption ? (
              <Hint label={installOption.label}>
                <Button variant="ghost" size="icon-sm" onClick={installOption.install} aria-label={installOption.label}>
                  <Download />
                </Button>
              </Hint>
            ) : null}
            <Hint label="Settings">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={openSettings}
                aria-label="Settings"
                aria-current={settingsActive ? "page" : undefined}
                className={cn(settingsActive && "bg-accent/60 text-accent-foreground")}
              >
                <Settings />
              </Button>
            </Hint>
            <Hint label="Sign out">
              <Button variant="ghost" size="icon-sm" onClick={() => void logout()} aria-label="Sign out">
                <LogOut />
              </Button>
            </Hint>
          </div>
        </div>
      )}
    </aside>
    {pointMenu}
    </>
  );
}

/** What the desktop workspace hands the navigation. */
export interface NavWindows {
  /** Open windows, for the Windows entry's count. */
  count: number;
  /** The Windows menu around a trigger, opening on `side`. */
  menu: (
    trigger: React.ReactElement,
    side: "right" | "left" | "top" | "bottom",
    held?: { open: boolean; onOpenChange: (open: boolean) => void },
  ) => React.ReactNode;
  /** A destination's right-click / hold menu. */
  onOpen: (view: SidebarView, where: "here" | "right" | "below") => void;
  onHide: () => void;
  /** The logo pressed: dragging it docks the navigation at an edge. */
  onDockDragStart: (event: React.PointerEvent) => void;
  /** The Layouts entry: saved layouts, the one applied, and its menu around a trigger. */
  layouts: {
    count: number;
    activeName: string | null;
    menu: (trigger: React.ReactElement, side: "right" | "left" | "top" | "bottom", hint?: string) => React.ReactNode;
  };
}

type ViewDragProps = {
  draggable?: boolean;
  "data-nav-view"?: string;
  onDragStart?: (event: React.DragEvent) => void;
};

/** The Windows entry, first in More: an icon (rail, strip) or a row, with the count of open windows. */
const WindowsButton = forwardRef<HTMLButtonElement, { variant: "rail" | "row"; count: number } & React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ variant, count, className, ...props }, ref) =>
    variant === "rail" ? (
      <button
        ref={ref}
        type="button"
        aria-label={`Windows · ${count} open`}
        title={`Windows · ${count} open`}
        className={cn(
          "relative grid size-9 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground data-[state=open]:bg-muted/50 data-[state=open]:text-foreground [&_svg]:size-[18px]",
          className,
        )}
        {...props}
      >
        <AppWindow />
        <span className="absolute -bottom-0.5 -right-0.5 min-w-4 rounded-full bg-muted px-1 text-center text-[9.5px] font-semibold leading-4 tabular-nums text-muted-foreground ring-2 ring-background">
          {count}
        </span>
      </button>
    ) : (
      <button
        ref={ref}
        type="button"
        className={cn(
          "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground data-[state=open]:bg-muted/50 data-[state=open]:text-foreground [&_svg]:size-4 [&_svg]:shrink-0",
          className,
        )}
        {...props}
      >
        <AppWindow />
        <span className="flex-1 text-left">Windows</span>
        <span className="rounded-full bg-muted px-1.5 text-[10px] font-semibold tabular-nums">{count}</span>
      </button>
    ),
);
WindowsButton.displayName = "WindowsButton";

/**
 * The Layouts entry, after Apps: an icon with the number saved (rail, strip) or a row
 * naming the layout applied, "Layouts" when none is.
 */
const LayoutsButton = forwardRef<
  HTMLButtonElement,
  { variant: "rail" | "row"; count: number; activeName: string | null } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ variant, count, activeName, className, ...props }, ref) =>
  variant === "rail" ? (
    <button
      ref={ref}
      type="button"
      aria-label={`Layouts · ${count} saved${activeName ? ` · ${activeName}` : ""}`}
      aria-keyshortcuts="Alt+L Alt+Shift+L"
      className={cn(
        "relative grid size-9 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground data-[state=open]:bg-muted/50 data-[state=open]:text-foreground [&_svg]:size-[18px]",
        className,
      )}
      {...props}
    >
      <LayoutTemplate />
      {count > 0 ? (
        <span className="absolute -bottom-0.5 -right-0.5 min-w-4 rounded-full bg-muted px-1 text-center text-[9.5px] font-semibold leading-4 tabular-nums text-muted-foreground ring-2 ring-background">
          {count}
        </span>
      ) : null}
    </button>
  ) : (
    <button
      ref={ref}
      type="button"
      aria-keyshortcuts="Alt+L Alt+Shift+L"
      title={activeName ? `Layout: ${activeName} · Alt+L next, Alt+Shift+L previous` : "Saved layouts · Alt+L next, Alt+Shift+L previous"}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground data-[state=open]:bg-muted/50 data-[state=open]:text-foreground [&_svg]:size-4 [&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      <LayoutTemplate />
      <span className="min-w-0 flex-1 truncate text-left">{activeName ?? "Layouts"}</span>
      {count > 0 ? <span className="rounded-full bg-muted px-1.5 text-[10px] font-semibold tabular-nums">{count}</span> : null}
    </button>
  ),
);
LayoutsButton.displayName = "LayoutsButton";

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join("") || "?";

const railLabel = (item: NavItem): string =>
  [item.label, item.count ? String(item.count) : null, item.review ? `${item.review} to review` : null].filter(Boolean).join(" · ");

/** Cards to review beside My tasks' own count: its own colour and a label, so the two never read as one number. */
const ReviewPill = ({ count }: { count?: number }) =>
  count ? (
    <span
      title={`${count} ready for your review`}
      aria-label={`${count} to review`}
      className="rounded-full border px-1.5 text-[10px] font-medium tabular-nums"
      style={{ color: "var(--kind-review)", borderColor: "color-mix(in oklab, var(--kind-review) 40%, transparent)" }}
    >
      {count} review
    </span>
  ) : null;

const CountPill = ({ count, tint }: { count?: number; tint?: string }) =>
  count !== undefined && count > 0 ? (
    <span
      className="rounded-full px-1.5 text-[10px] font-semibold tabular-nums"
      style={{
        color: tint ?? "var(--muted-foreground)",
        backgroundColor: `color-mix(in oklab, ${tint ?? "var(--muted-foreground)"} 16%, transparent)`,
      }}
    >
      {count}
    </span>
  ) : null;

/** A count that asks for the user now: solid, like the rail's corner pills. */
const SolidPill = ({ count, tint }: { count?: number; tint?: string }) =>
  count !== undefined && count > 0 ? (
    <span
      className="min-w-5 rounded-full px-1.5 text-center text-[10.5px] font-semibold leading-[18px] tabular-nums text-background"
      style={{ backgroundColor: tint ?? "var(--muted-foreground)" }}
    >
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

/**
 * An icon in the rail. A count sits on its corner as a solid pill (the rail has
 * no room for a label beside it); `dot` is a count-less marker, such as a board
 * running late. Forwards its ref and props so it can be a menu or tooltip trigger.
 */
const RailButton = forwardRef<HTMLButtonElement, { item: NavItem; dot?: string } & React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ item, dot, className, ...props }, ref) => {
    const count = item.count ?? 0;
    return (
      <button
        ref={ref}
        type="button"
        aria-label={railLabel(item)}
        aria-current={item.active ? "page" : undefined}
        className={cn(
          "relative grid size-9 place-items-center rounded-md transition-colors [&_svg]:size-[18px]",
          item.active
            ? "bg-accent/60 text-accent-foreground"
            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground data-[state=open]:bg-muted/50 data-[state=open]:text-foreground",
          className,
        )}
        {...props}
      >
        <span className="contents" style={item.active && item.tint ? { color: item.tint } : undefined}>
          {item.icon}
        </span>
        {count > 0 ? (
          <span
            className="absolute -right-1 -top-1 min-w-4 rounded-full px-1 text-center text-[9.5px] font-semibold leading-4 tabular-nums text-background ring-2 ring-background"
            style={{ backgroundColor: item.tint ?? "var(--muted-foreground)" }}
          >
            {count > 99 ? "99+" : count}
          </span>
        ) : dot ? (
          <span className="absolute right-1 top-1 size-2 rounded-full ring-2 ring-background" style={{ backgroundColor: dot }} />
        ) : null}
      </button>
    );
  },
);
RailButton.displayName = "RailButton";

/** A board in the rail's Boards menu: its name, its pills and the time left. */
const BoardMenuItem = ({
  detail,
  now,
  active,
  awaiting,
  onSelect,
}: {
  detail: BoardDetail;
  now: number;
  active: boolean;
  awaiting: number;
  onSelect: () => void;
}) => (
  <DropdownMenuItem onSelect={onSelect} className={cn("flex-col items-stretch gap-0.5", active && "bg-accent/50")}>
    <span className="flex items-center gap-2">
      <span className={cn("min-w-0 flex-1 truncate text-[13px]", active && "font-medium")}>{detail.board.name}</span>
      <CountPill count={awaiting} tint="var(--waiting)" />
      <CountPill count={detail.stats.overdue} tint="var(--destructive)" />
    </span>
    <span className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
      <span className="truncate">{detail.window.label}</span>
      <span className="shrink-0 font-medium tabular-nums" style={{ color: urgencyColor(detail.window, now) }}>
        {remainingLabel(detail.window, now)}
      </span>
    </span>
  </DropdownMenuItem>
);

/**
 * One board in the sidebar. `onArchive` is passed only for an expired board —
 * the row's own way out of the list, so clearing a closed board does not mean
 * opening it first to find the menu. Archiving is reversible, which is why it is
 * one click with no confirm.
 */
const BoardRow = ({
  detail,
  now,
  active,
  awaiting,
  onSelect,
  onArchive,
  drag,
}: {
  detail: BoardDetail;
  now: number;
  active: boolean;
  /** Cards on it waiting on the user's answer. */
  awaiting: number;
  onSelect: () => void;
  onArchive?: () => void;
  drag?: ViewDragProps;
}) => {
  const tint = urgencyColor(detail.window, now);
  const outstanding = detail.stats.total - detail.stats.done;
  return (
    <div className="group relative">
      <button
        onClick={onSelect}
        {...drag}
        className={cn(
          "w-full rounded-md border px-2.5 py-2 text-left transition-all duration-150",
          active
            ? "border-ring/45 bg-accent/45 shadow-sm"
            : "border-transparent hover:border-border hover:bg-muted/50",
          onArchive && "opacity-80 hover:opacity-100",
        )}
      >
        <div className="flex items-center gap-2">
          <p className={cn("min-w-0 flex-1 truncate text-[13px] font-medium", onArchive && "pr-6")}>
            {detail.board.name}
          </p>
          {awaiting > 0 ? (
            <span
              className="shrink-0 rounded-full bg-waiting/15 px-1.5 text-[10px] font-semibold text-waiting"
              title={`${awaiting} card(s) waiting on your answer`}
            >
              {awaiting}
            </span>
          ) : null}
          {detail.stats.overdue > 0 ? (
            <span className="shrink-0 rounded-full bg-destructive/15 px-1.5 text-[10px] font-semibold text-destructive">
              {detail.stats.overdue}
            </span>
          ) : null}
        </div>
        <p className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
          <span className="truncate">{detail.window.label}</span>
          <span className="shrink-0 font-medium tabular-nums" style={{ color: tint }}>
            {remainingLabel(detail.window, now)}
          </span>
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          <Progress value={progressPercent(detail.window, now)} indicatorColor={tint} className="h-1" />
          <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
            {outstanding === 0 && detail.stats.total > 0 ? "clear" : `${outstanding} left`}
          </span>
        </div>
      </button>
      {onArchive ? (
        <Hint label="Move to the archive — nothing is deleted, and it can be restored">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Archive ${detail.board.name}`}
            onClick={onArchive}
            className="absolute right-1 top-1 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
          >
            <Archive />
          </Button>
        </Hint>
      ) : null}
    </div>
  );
};

const NavRow = ({ item, onClick, drag }: { item: NavItem; onClick: () => void; drag?: ViewDragProps }) => (
  <button
    type="button"
    onClick={onClick}
    {...drag}
    aria-current={item.active ? "page" : undefined}
    className={cn(
      "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors",
      "[&_svg]:size-4 [&_svg]:shrink-0",
      item.active
        ? "bg-accent/50 font-medium text-accent-foreground"
        : item.emphasis
          ? "font-medium hover:bg-muted/50"
          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
    )}
    style={
      item.emphasis && item.tint
        ? { color: item.tint, backgroundColor: `color-mix(in oklab, ${item.tint} ${item.active ? 20 : 12}%, transparent)` }
        : undefined
    }
  >
    <span style={(item.active || item.emphasis) && item.tint ? { color: item.tint } : undefined}>{item.icon}</span>
    <span className="flex-1 text-left">{item.label}</span>
    <ReviewPill count={item.review} />
    {item.emphasis ? <SolidPill count={item.count} tint={item.tint} /> : <CountPill count={item.count} tint={item.tint} />}
  </button>
);

type MenuSide = "right" | "left" | "top" | "bottom";

/** The Boards menu of the rail and of a top or bottom strip: every board, then New board. */
function BoardsDropdown({
  running,
  expired,
  total,
  now,
  activeBoardId,
  awaitingByBoard,
  boardAwaiting,
  boardOverdue,
  canEdit,
  onSelect,
  onCreateBoard,
  side,
  align,
}: {
  running: BoardDetail[];
  expired: BoardDetail[];
  total: number;
  now: number;
  activeBoardId: string | null;
  awaitingByBoard?: Map<string, number>;
  boardAwaiting: number;
  boardOverdue: number;
  canEdit: boolean;
  onSelect: (view: SidebarView) => void;
  onCreateBoard: () => void;
  side: MenuSide;
  align: "start" | "end";
}) {
  const item = (detail: BoardDetail) => (
    <BoardMenuItem
      key={detail.board.id}
      detail={detail}
      now={now}
      active={activeBoardId === detail.board.id}
      awaiting={awaitingByBoard?.get(detail.board.id) ?? 0}
      onSelect={() => onSelect({ kind: "board", boardId: detail.board.id })}
    />
  );
  return (
    <DropdownMenu>
      <Hint label={`Boards · ${running.length}`} side={side}>
        <DropdownMenuTrigger asChild>
          <RailButton
            item={{
              key: "boards",
              icon: <SquareKanban />,
              label: "Boards",
              active: activeBoardId !== null,
              count: boardAwaiting > 0 ? boardAwaiting : undefined,
              tint: "var(--waiting)",
              view: { kind: "boards" },
            }}
            dot={boardAwaiting === 0 && boardOverdue > 0 ? "var(--destructive)" : undefined}
          />
        </DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent side={side} align={align} className="w-64">
        <DropdownMenuLabel>Boards</DropdownMenuLabel>
        <div className="max-h-[60dvh] overflow-y-auto scrollbar-slim">
          {running.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              {total === 0 ? "No boards yet." : "Every board's window has closed."}
            </p>
          ) : null}
          {running.map(item)}
          {expired.length > 0 ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Expired</DropdownMenuLabel>
              {expired.map(item)}
            </>
          ) : null}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={!canEdit} onSelect={onCreateBoard}>
          <Plus />
          New board
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The registers (Activity, Projects) behind one icon, lifting any count that asks for something. */
function MoreDropdown({
  secondary,
  moreSummary,
  moreActive,
  moreAlerts,
  onSelect,
  windows,
  side,
  align,
}: {
  secondary: NavItem[];
  moreSummary: string;
  moreActive: NavItem | undefined;
  moreAlerts: NavItem[];
  onSelect: (view: SidebarView) => void;
  windows?: NavWindows;
  side: MenuSide;
  align: "start" | "end";
}) {
  // The Windows menu is its own dropdown: the item closes More, then opens it anchored beside More's button.
  const [windowsOpen, setWindowsOpen] = useState(false);
  const wantWindows = useRef(false);
  return (
    <>
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open && wantWindows.current) {
          wantWindows.current = false;
          setWindowsOpen(true);
        }
      }}
    >
      <Hint label={moreSummary ? `More · ${moreSummary}` : "More"} side={side}>
        <DropdownMenuTrigger asChild>
          <RailButton
            item={{
              key: "more",
              icon: <Ellipsis />,
              label: "More",
              active: moreActive !== undefined,
              count: moreAlerts[0]?.count,
              tint: moreAlerts[0]?.tint,
              view: { kind: "status" },
            }}
          />
        </DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent side={side} align={align} className="w-56">
        <DropdownMenuLabel>More</DropdownMenuLabel>
        {windows ? (
          <DropdownMenuItem onSelect={() => { wantWindows.current = true; }}>
            <AppWindow />
            <span className="flex-1">Windows</span>
            <span className="rounded-full bg-muted px-1.5 text-[10px] font-semibold tabular-nums">{windows.count}</span>
          </DropdownMenuItem>
        ) : null}
        {secondary.map((item) => (
          <DropdownMenuItem
            key={item.key}
            onSelect={() => onSelect(item.view)}
            className={cn(item.active && "bg-accent/50 font-medium")}
          >
            {item.icon}
            <span className="flex-1">{item.label}</span>
            <CountPill count={item.count} tint={item.tint} />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
    {windows ? windows.menu(<span aria-hidden className="pointer-events-none size-0" />, side, { open: windowsOpen, onOpenChange: setWindowsOpen }) : null}
    </>
  );
}
